// The Zooylo Sales portal (/sales) — grows the owner console (phase 2 of
// docs/revamp/SALES_PORTAL_REQUIREMENTS.md; design §2D of
// docs/superpowers/specs/2026-10-23-premium-lifecycle-org-consent-sales-portal-design.md).
//
// GATE. Signed out → the admin sign-in. Signed in → sales_me(); anyone not on
// the Zooylo team gets an ordinary not-found screen, so the portal's existence
// is not advertised. Every RPC behind it checks the role again (42501), so the
// gate and the hidden buttons are courtesy, not the control.
//
// Demo mode (no Supabase) runs over in-memory demo data with a role switcher.

import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary, LOCALE_LABEL, type Locale } from "../../i18n/dictionary";
import { getSalesDictionary } from "../../i18n/salesDictionary";
import { shouldUseSupabase } from "../../data";
import { getSalesData, roleCan, SALES_ROLES, type SalesMe, type SalesRole } from "../../data/sales";
import { setDemoSalesRole } from "../../data/salesDemo";
import { useSession } from "../../lib/auth";
import { getSupabaseClient } from "../../lib/supabase";
import { AdminSignIn } from "../auth/AdminSignIn";
import { LinkSent } from "../auth/LinkSent";
import { sendMagicLink, adminRedirectUrl } from "../auth/sendMagicLink";
import { NotFound } from "../console/Console";
import { Button, INPUT, Notice, useLoad } from "../admin/ui";
import { SalesProvider, useSales } from "./shared";
import { AccountsList } from "./Accounts";
import { AccountDetail } from "./AccountDetail";
import { Pipeline } from "./Pipeline";
import { Codes } from "./Codes";
import { BillingRun } from "./BillingRun";
import { Receivables } from "./Receivables";
import { Reports } from "./Reports";
import { Team } from "./Team";
import "../../components/portal.css";

export function SalesGate() {
  return shouldUseSupabase() ? <SalesGateLive /> : <SalesDemo />;
}

function SalesGateLive() {
  const { locale, setLocale } = useComfort();
  const session = useSession();
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (session.status === "loading") return <Splash />;
  if (session.status === "signed_out") {
    if (sentTo) {
      return (
        <LinkSent
          locale={locale}
          email={sentTo}
          onResend={() => void sendMagicLink(sentTo, adminRedirectUrl())}
          onDifferentEmail={() => setSentTo(null)}
        />
      );
    }
    return <AdminSignIn locale={locale} setLocale={setLocale} onSent={setSentTo} />;
  }
  return <SalesAuthorised />;
}

function SalesAuthorised() {
  const { data, loading } = useLoad(() => getSalesData().me(), []);
  if (loading) return <Splash />;
  if (!data) return <NotFound />;
  return (
    <SalesShell
      me={data}
      onSignOut={() => {
        void getSupabaseClient().auth.signOut().catch(() => {});
      }}
    />
  );
}

function SalesDemo() {
  const [me, setMe] = useState<SalesMe | null>(null);
  useEffect(() => {
    let live = true;
    void getSalesData()
      .me()
      .then((m) => live && setMe(m));
    return () => {
      live = false;
    };
  }, []);
  if (!me) return <Splash />;
  return (
    <SalesShell
      demo
      me={me}
      onRole={(role) => {
        setDemoSalesRole(role);
        setMe({ ...me, role });
      }}
    />
  );
}

function Splash() {
  return (
    <div className="zp-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <span aria-label="Loading" style={{ width: 28, height: 28, border: "3px solid var(--line-strong)", borderTopColor: "var(--brand)", borderRadius: "50%", animation: "zspin .8s linear infinite" }} />
    </div>
  );
}

export function SalesShell({
  me,
  demo = false,
  onSignOut,
  onRole,
}: {
  me: SalesMe;
  demo?: boolean;
  onSignOut?: () => void;
  onRole?: (r: SalesRole) => void;
}) {
  const { locale, setLocale } = useComfort();
  const t = getSalesDictionary(locale);
  const common = getDictionary(locale).common;
  const next: Locale = locale === "en" ? "ms" : "en";
  const ctx = useMemo(() => ({ ds: getSalesData(), me, t, locale, demo }), [me, t, locale, demo]);

  return (
    <SalesProvider value={ctx}>
      <div className="zp-root" style={{ fontFamily: "var(--font-body)", color: "var(--ink)", background: "var(--bg)", minHeight: "100vh" }}>
        <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 22, color: "var(--brand)" }}>zooylo</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-faint)" }}>{t.shell.eyebrow}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
              {demo && onRole ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
                  {t.shell.viewingAs}
                  <select value={me.role} onChange={(e) => onRole(e.target.value as SalesRole)} style={{ ...INPUT, width: "auto", minHeight: 44 }}>
                    {SALES_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t.roles[r]}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span style={{ fontSize: 13.5, color: "var(--ink-soft)", overflowWrap: "anywhere" }}>
                  {fill(t.shell.signedInAs, { email: me.email, role: t.roles[me.role] })}
                </span>
              )}
              <Button variant="secondary" onClick={() => setLocale(next)} lang={next}>
                {LOCALE_LABEL[next]}
              </Button>
              {onSignOut && (
                <Button variant="secondary" onClick={onSignOut}>
                  {common.signOut}
                </Button>
              )}
            </div>
          </div>
          <SalesNav />
        </header>
        <main style={{ maxWidth: 1200, margin: "0 auto", padding: "22px 16px 60px" }}>
          <h1 style={{ fontSize: 28, color: "var(--ink-deep)", margin: 0 }}>{t.shell.title}</h1>
          <p style={{ color: "var(--ink-soft)", fontSize: 16, margin: "6px 0 16px" }}>{t.shell.subtitle}</p>
          {demo && (
            <div style={{ marginBottom: 16 }}>
              <Notice tone="info">{t.shell.demoNote}</Notice>
            </div>
          )}
          <Routes>
            <Route index element={<AccountsList />} />
            <Route path="accounts" element={<Navigate to=".." replace />} />
            <Route path="accounts/:orgId" element={<AccountDetail />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="codes" element={<Codes />} />
            <Route path="billing" element={<BillingRun />} />
            <Route path="receivables" element={<Receivables />} />
            <Route path="reports" element={<Reports />} />
            <Route path="team" element={<Team />} />
            <Route path="*" element={<Navigate to="/sales" replace />} />
          </Routes>
        </main>
      </div>
    </SalesProvider>
  );
}

function SalesNav() {
  const { t, me } = useSales();
  const items: { to: string; label: string; end?: boolean }[] = [
    { to: "/sales", label: t.shell.navAccounts, end: true },
    { to: "/sales/pipeline", label: t.shell.navPipeline },
    { to: "/sales/codes", label: t.shell.navCodes },
    { to: "/sales/billing", label: t.shell.navBilling },
    { to: "/sales/receivables", label: t.shell.navReceivables },
    { to: "/sales/reports", label: t.shell.navReports },
    ...(roleCan(me.role, "team") ? [{ to: "/sales/team", label: t.shell.navTeam }] : []),
  ];
  return (
    <nav aria-label={t.shell.nav} style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px 12px" }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {items.map((i) => (
          <li key={i.to}>
            <NavLink
              to={i.to}
              end={i.end}
              style={({ isActive }) => ({
                display: "inline-flex",
                alignItems: "center",
                minHeight: 44,
                padding: "0 14px",
                border: "1px solid var(--line-strong)",
                borderRadius: "var(--r-pill)",
                background: isActive ? "var(--brand-tint)" : "var(--surface)",
                color: isActive ? "var(--brand-hover)" : "var(--ink-soft)",
                fontWeight: isActive ? 700 : 600,
                fontSize: 14.5,
                textDecoration: "none",
              })}
            >
              {i.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
